// VIB-951: mail-vennlig eksport av et mersalg til kunde.
//
// Produserer BÅDE rik tekst (HTML-tabell, limes inn formatert i Gmail/Outlook)
// og ren tekst (fallback). Kunde-vendt: viser kun beskrivelse, mengde og sum —
// aldri innkjøp/margin/BF. Totaler (påslag/rabatt/sum) speiler tilbudseksporten.

import { formatNOK } from "./format";
import { PHASE_LABEL, PHASE_ORDER } from "@/components/offer/offer-phase-tokens";
import type { OfferPhase } from "@/lib/offer-calc";

export interface UpsellExportLine {
  phase: string | null;
  description: string | null;
  quantity: number | null;
  unit: string | null;
  amount: number | null;
}

export interface UpsellExportInput {
  title: string;
  lines: UpsellExportLine[];
  /** upsells.overhead_pct — null = bruk prosjektets default. */
  overhead_pct: number | null;
  discount_pct: number | null;
  discount_amount: number | null;
  /** Prosjektets default overhead-prosent. */
  defaultOverheadPct: number;
}

export interface UpsellExport {
  html: string;
  text: string;
}

function qtyLabel(quantity: number | null, unit: string | null): string {
  const q = Number(quantity ?? 0);
  if (q <= 0 || !unit) return "";
  // Strip overflødig .0 (10.0 → 10, men 1.5 beholdes).
  const num = Number.isInteger(q) ? String(q) : String(q);
  return `${num} ${unit}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

interface Totals {
  lineSum: number;
  overheadPct: number;
  overheadAmount: number;
  discount: number;
  total: number;
}

/** Speiler beregningen i UpsellEditDialog (kunde-total). */
export function calcUpsellExportTotals(input: UpsellExportInput): Totals {
  const lineSum = input.lines.reduce((s, l) => s + Number(l.amount ?? 0), 0);
  const overheadPct = input.overhead_pct ?? input.defaultOverheadPct;
  const overheadAmount = (lineSum * overheadPct) / 100;
  const baseWithOverhead = lineSum + overheadAmount;
  const discountFromAmount = input.discount_amount ?? 0;
  const discountFromPct = input.discount_pct
    ? (baseWithOverhead * input.discount_pct) / 100
    : 0;
  const discount = discountFromAmount > 0 ? discountFromAmount : discountFromPct;
  return {
    lineSum,
    overheadPct,
    overheadAmount,
    discount,
    total: baseWithOverhead - discount,
  };
}

/** Grupper linjer per fase i kanonisk faserekkefølge. */
function groupByPhase(lines: UpsellExportLine[]): Array<{ phase: OfferPhase; lines: UpsellExportLine[] }> {
  const map = new Map<OfferPhase, UpsellExportLine[]>();
  for (const l of lines) {
    const phase = (PHASE_LABEL[l.phase as OfferPhase] ? l.phase : "production") as OfferPhase;
    if (!map.has(phase)) map.set(phase, []);
    map.get(phase)!.push(l);
  }
  return PHASE_ORDER.filter((p) => map.has(p)).map((p) => ({ phase: p, lines: map.get(p)! }));
}

export function buildUpsellExport(input: UpsellExportInput): UpsellExport {
  const groups = groupByPhase(input.lines);
  const t = calcUpsellExportTotals(input);

  // ── Ren tekst ──
  const textLines: string[] = [];
  textLines.push(`MERSALG: ${input.title}`, "");
  for (const g of groups) {
    textLines.push(PHASE_LABEL[g.phase]);
    for (const l of g.lines) {
      const q = qtyLabel(l.quantity, l.unit);
      const parts = [l.description?.trim() || "(uten navn)"];
      if (q) parts.push(q);
      parts.push(formatNOK(l.amount ?? 0));
      textLines.push(`• ${parts.join(" — ")}`);
    }
    textLines.push("");
  }
  if (t.overheadAmount > 0) textLines.push(`Overhead (${t.overheadPct} %): +${formatNOK(t.overheadAmount)}`);
  if (t.discount > 0) textLines.push(`Rabatt: −${formatNOK(t.discount)}`);
  textLines.push(`Totalt: ${formatNOK(t.total)} eks. mva`);
  const text = textLines.join("\n");

  // ── Rik tekst (HTML-tabell, inline styles for paste i mail) ──
  // Minimalistisk + full bredde: ingen grå fase-bånd, ingen per-rad-streker.
  // Kun én strek under header og over total.
  const cellL = "padding:5px 0;";
  const cellR = "padding:5px 0;text-align:right;white-space:nowrap;";
  const rule = "1.5px solid #111827";

  const head =
    `<tr style="border-bottom:${rule};">` +
    `<th style="${cellL}font-weight:600;">Linje</th>` +
    `<th style="${cellR}font-weight:600;width:90px;">Mengde</th>` +
    `<th style="${cellR}font-weight:600;width:120px;">Sum</th></tr>`;

  const body: string[] = [];
  for (const g of groups) {
    body.push(
      `<tr><td colspan="3" style="padding:14px 0 2px;font-weight:bold;">${escapeHtml(PHASE_LABEL[g.phase])}</td></tr>`,
    );
    for (const l of g.lines) {
      body.push(
        `<tr><td style="${cellL}">${escapeHtml(l.description?.trim() || "(uten navn)")}</td>` +
          `<td style="${cellR}color:#6b7280;">${escapeHtml(qtyLabel(l.quantity, l.unit))}</td>` +
          `<td style="${cellR}">${escapeHtml(formatNOK(l.amount ?? 0))}</td></tr>`,
      );
    }
  }

  const foot: string[] = [];
  if (t.overheadAmount > 0) {
    foot.push(
      `<tr><td colspan="2" style="${cellR}color:#6b7280;">Overhead (${t.overheadPct} %)</td>` +
        `<td style="${cellR}color:#6b7280;">+${escapeHtml(formatNOK(t.overheadAmount))}</td></tr>`,
    );
  }
  if (t.discount > 0) {
    foot.push(
      `<tr><td colspan="2" style="${cellR}color:#6b7280;">Rabatt</td>` +
        `<td style="${cellR}color:#6b7280;">−${escapeHtml(formatNOK(t.discount))}</td></tr>`,
    );
  }
  foot.push(
    `<tr style="border-top:${rule};"><td colspan="2" style="${cellR}font-weight:bold;padding-top:8px;">Totalt eks. mva</td>` +
      `<td style="${cellR}font-weight:bold;padding-top:8px;">${escapeHtml(formatNOK(t.total))}</td></tr>`,
  );

  const html =
    `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#111827;max-width:640px;">` +
    `<p style="font-weight:bold;font-size:15px;margin:0 0 10px;">Mersalg: ${escapeHtml(input.title)}</p>` +
    `<table style="border-collapse:collapse;width:100%;font-size:14px;">` +
    `<thead>${head}</thead>` +
    `<tbody>${body.join("")}</tbody>` +
    `<tfoot>${foot.join("")}</tfoot>` +
    `</table></div>`;

  return { html, text };
}
